import { Injectable } from '@angular/core';
import { isNullish } from '@commonHelpers/math-utils';
import { RequestService } from '@commonServices/request.service';
import { Client as AnalyticsApi, CrossCatalogViewSearchFilterTagsDto, CrossCatalogViewSearchTermsDto, CrossCatalogViewSearchesAvgDto, CrossCatalogViewSearchesBenchmarkDto, CrossCatalogViewSearchesDto, CrossCatalogViewSearchesPerDateDto, CrossCatalogViewSearchesPerMonthDto, CrossCatalogViewSearchesPerWeekDto, DatePickerRange } from '@interfaces/HttpClient/AnalyticsApiModels';
import { firstValueFrom } from 'rxjs';
import { DaterangeService } from './daterange.service';


@Injectable()
export class CatalogDashboardCrossCatalogViewSearchService {

	constructor(
		private analyticsApi: AnalyticsApi,
		private requestService: RequestService,
		private daterangeService: DaterangeService) { }

	async getCrossCatalogViewSearchesCountOfCatalogAsync(catalogId: number, dateRange?: DatePickerRange): Promise<CrossCatalogViewSearchesDto[]> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchesCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchesCountOfCatalog(catalogId, range.rangeStart, range.rangeEnd));
			}

		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogViewSearchesCountBenchmark(catalogId: number, dateRange: DatePickerRange): Promise<CrossCatalogViewSearchesBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetCrossCatalogViewSearchesCountBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getCrossCatalogViewSearchesCountCategoryBenchmark(catalogId: number, dateRange: DatePickerRange): Promise<CrossCatalogViewSearchesBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetCrossCatalogViewSearchesCountCategoryBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getCrossCatalogViewSearchesCountCategoryGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CrossCatalogViewSearchesBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetCrossCatalogViewSearchesCountCategoryGroupBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogViewSearchTermsCountOfCatalogAsync(catalogId: number): Promise<CrossCatalogViewSearchTermsDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchTermsCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogViewSearchFilterTagsCountOfCatalogAsync(catalogId: number): Promise<CrossCatalogViewSearchFilterTagsDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchFilterTagsCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogViewSearchesCountOfCatalogAVGAsync(catalogId: number): Promise<CrossCatalogViewSearchesAvgDto> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchesCountOfCatalogAvg(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogViewSearchesCountOfCatalogPerDateAsync(catalogId: number): Promise<CrossCatalogViewSearchesPerDateDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchesCountOfCatalogPerDate(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogViewSearchesCountOfCatalogPerWeekAsync(catalogId: number): Promise<CrossCatalogViewSearchesPerWeekDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchesCountOfCatalogPerWeek(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogViewSearchesCountOfCatalogPerMonthAsync(catalogId: number): Promise<CrossCatalogViewSearchesPerMonthDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogViewSearch_GetCrossCatalogViewSearchesCountOfCatalogPerMonth(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
}
